/* eslint-disable max-lines-per-function */
/* eslint-disable new-cap */
import {debounced} from '@mvps-genesis/utilities';
import 'extensions/multi-system/components/x-room';
import 'extensions/multi-system/components/x-room-progress';
import 'extensions/multi-system/components/x-section';
import 'extensions/multi-system/components/x-step-indoor';
import 'extensions/multi-system/components/x-step-outdoor';
import 'extensions/multi-system/components/x-step-room-creation';
import 'extensions/multi-system/components/x-step-required-products';
import 'extensions/multi-system/components/x-step-upsell';
import 'extensions/multi-system/components/x-summary';
import {CAPACITY_FILTER_FIELD, DEFAULT_MULTI_SYSTEM_STATE, STATE_ID_QUERY_PARAM, StateActions, Steps} from 'extensions/multi-system/data/constants';
import {multiSystemStore} from 'extensions/multi-system/data/multi-system-store';
import 'extensions/ui-components/x-drawer';
import 'extensions/ui-components/x-flag';
import {defineCustomElement} from 'extensions/ui-utils';
import {LitElement, html, nothing} from 'lit';
import stylesheet from './x-multi-system.scss?as=stylesheet';

/**
 * X-multi-system -- Root component. Responsible for the following:
 *1. Handle store / state.
 *2. Handle URL updates.
 *3. Handle popstate.
 *4. Listen for events from subcomponents.
 *5. SCSS for header styles.
 *6. Handle step templates via switch statement.
 *7. Render templates for summary drawer (via x-sticky-drawer UI component).
 */

export class MultiSystem extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			disallowedCombinations: {
				attribute: 'disallowed-combinations',
				type: Array
			},
			masterMatrix: {
				attribute: 'master-matrix',
				type: Array
			},
			state: {
				state: true,
				type: Object
			},
			totalCapacity: {
				state: true,
				type: Number
			}
		};
	}

	get selectedRoomName () {
		switch (this.state?.step) {
			case Steps.Outdoor:
				return 'Outdoor Unit';
			case Steps.RequiredProducts:
				return 'Required Parts';
			case Steps.Upsell:
				return 'Optional Add-Ons';
			case Steps.Review:
				return 'Review';
			default:
				return this.state?.rooms?.[this.state?.selectedRoom]?.name;
		}
	}

	constructor () {
		super();

		// Set default values

		/**
		 * @type {import('extensions/multi-system/types').MasterMatrix}
		 */
		this.masterMatrix = [];

		/**
		 * @type {import('extensions/multi-system/types').MultiSystemState}
		 */
		this.state = this._getStateFromUrl();

		/**
		 * @type {number}
		 */
		this.totalCapacity = 0;

		/**
		 * @type {Array<{selectedIndoors: Array<string>, disallowedOutdoors: Array<string>, selectedCapacities?: Array<number>, disallowedCapacityIndoors?: Array<{capacity: number, product: string}>}>}
		 * Example: [
		 *   {
		 *     selectedIndoors: ["AGTG09KVCA"],
		 *     disallowedOutdoors: ["AOTH36KBTA5"]
		 *   },
		 *   {
		 *     selectedIndoors: ["AGTG12KVCA"],
		 *     disallowedOutdoors: ["AOTH36KBTA5"]
		 *   },
		 *   {
		 *     selectedCapacities: [7, 7, 18],
		 *     disallowedCapacityIndoors: [
		 *       {
		 *         capacity: 18,
		 *         product: "ARTH18KMTAP"
		 *       }
		 *     ],
		 *     disallowedOutdoors: ["AOTH24KBCA3"]
		 *   }
		 * ]
		 */
		this.disallowedCombinations = [];

		// Bind handlers
		this.handlePopstate = debounced(200, this._handlePopstate.bind(this));
		this.handleStateChange = this._handleStateChange.bind(this);
	}

	_getStateFromUrl () {
		const url = new URL(window.location.href);
		const stateId = url.searchParams.get(STATE_ID_QUERY_PARAM);

		return multiSystemStore.get(stateId, DEFAULT_MULTI_SYSTEM_STATE);
	}

	_setUrlFromState () {
		const url = new URL(window.location.href);

		// Only set history if state id does not match
		if (url.searchParams?.get(STATE_ID_QUERY_PARAM) !== this.state?.id) {
			url.searchParams.set(STATE_ID_QUERY_PARAM, this.state.id);
			window.history.pushState(this.state.id, '', url.toString());
		}
	}

	_handlePopstate () {
		this.state = this._getStateFromUrl();
	}

	/**
	 * @param {number} action
	 * @param {any} payload
	 * @param {boolean} merge
	 */
	_processStateAction (action, payload, merge) {
		switch (action) {
			case StateActions.StepUpdate: {
				return multiSystemStore.set(
					{
						step: payload || Steps.RoomCreation
					},
					merge
				);
			}
			case StateActions.RoomCountUpdate: {
				return multiSystemStore.set(
					{
						roomCount: payload || 0
					},
					merge
				);
			}
			case StateActions.RoomsUpdate: {
				return multiSystemStore.set(
					{
						rooms: payload || []
					},
					merge
				);
			}
			case StateActions.RoomUpdate: {
				const rooms = this.state?.rooms?.map((room, index) => {
					if (index === payload.index) {
						room.name = payload.name || room.name;
						room.selection = payload.selection || room.selection;
						room.product = payload.product || room.product;
						room.capacityClass = payload.capacityClass || room.capacityClass;
					}

					return room;
				});

				return multiSystemStore.set(
					{
						rooms
					},
					merge
				);
			}
			case StateActions.RoomNameUpdate: {
				const rooms = this.state?.rooms?.map((room, index) => {
					if (index === payload.index) {
						room.name = payload.name || room.name;
					}

					return room;
				});

				return multiSystemStore.set(
					{
						rooms
					},
					merge
				);
			}
			case StateActions.RoomSelectionUpdate: {
				const rooms = this.state?.rooms?.map((room, index) => {
					if (index === payload.index) {
						room.selection = payload.selection || room.selection;
					}

					return room;
				});

				return multiSystemStore.set(
					{
						rooms
					},
					merge
				);
			}
			case StateActions.RoomProductUpdate: {
				const rooms = this.state?.rooms?.map((room, index) => {
					if (index === payload.index) {
						room.product = payload.product || '';
						room.capacityClass = payload.capacityClass || '';
					}

					return room;
				});

				return multiSystemStore.set(
					{
						rooms
					},
					merge
				);
			}
			case StateActions.SelectedRoomUpdate: {
				return multiSystemStore.set(
					{
						selectedRoom: payload
					},
					merge
				);
			}
			case StateActions.UpsellProductsUpdate: {
				return multiSystemStore.set(
					{
						upsellProducts: payload || []
					},
					merge
				);
			}
			case StateActions.UpsellProductCodeUpdate: {
				const upsellProducts = this.state?.upsellProducts?.map(upsellProduct => {
					if (upsellProduct.code === payload.code) {
						upsellProduct.code = payload.code || upsellProduct.code;
					}

					return upsellProduct;
				});

				return multiSystemStore.set(
					{
						upsellProducts
					},
					merge
				);
			}
			case StateActions.UpsellProductQuantityUpdate: {
				const upsellProducts = this.state?.upsellProducts?.map(upsellProduct => {
					if (upsellProduct.code === payload.code) {
						upsellProduct.quantity = payload.quantity || upsellProduct.quantity;
					}

					return upsellProduct;
				});

				return multiSystemStore.set(
					{
						upsellProducts
					},
					merge
				);
			}
			case StateActions.UpsellProductAdd: {
				const upsellProducts = structuredClone(this.state?.upsellProducts);
				upsellProducts.push({
					code: payload.code,
					quantity: payload.quantity
				});

				return multiSystemStore.set(
					{
						upsellProducts
					},
					merge
				);
			}
			case StateActions.UpsellProductRemove: {
				const upsellProducts = structuredClone(this.state?.upsellProducts)
					?.filter(upsellProduct => upsellProduct.code !== payload);

				return multiSystemStore.set(
					{
						upsellProducts
					},
					merge
				);
			}
			case StateActions.RequiredProductsUpdate: {
				return multiSystemStore.set(
					{
						requiredProducts: payload || []
					},
					merge
				);
			}
			case StateActions.RequiredProductCodeUpdate: {
				const requiredProducts = this.state?.requiredProducts?.map(requiredProduct => {
					if (requiredProduct.code === payload.code) {
						requiredProduct.code = payload.code || requiredProduct.code;
					}

					return requiredProduct;
				});

				return multiSystemStore.set(
					{
						requiredProducts
					},
					merge
				);
			}
			case StateActions.RequiredProductQuantityUpdate: {
				const requiredProducts = this.state?.requiredProducts?.map(requiredProduct => {
					if (requiredProduct.code === payload.code) {
						requiredProduct.quantity = payload.quantity || requiredProduct.quantity;
					}

					return requiredProduct;
				});

				return multiSystemStore.set(
					{
						requiredProducts
					},
					merge
				);
			}
			case StateActions.RequiredProductAdd: {
				const requiredProducts = structuredClone(this.state?.requiredProducts) || [];
				requiredProducts.push({
					code: payload.code,
					quantity: payload.quantity
				});

				return multiSystemStore.set(
					{
						requiredProducts
					},
					merge
				);
			}
			case StateActions.RequiredProductRemove: {
				const requiredProducts = structuredClone(this.state?.requiredProducts)
					?.filter(requiredProduct => requiredProduct.code !== payload);

				return multiSystemStore.set(
					{
						requiredProducts
					},
					merge
				);
			}
			case StateActions.OutdoorProductUpdate: {
				return multiSystemStore.set(
					{
						outdoorProduct: payload || ''
					},
					merge
				);
			}
			case StateActions.RequiredProductsCount: {
				return multiSystemStore.set(
					{
						requiredProductsCount: payload || 0,
						requiredProductsCountInit: true
					},
					merge
				)
			}
			default:
				return this.state;
		}
	}

	async _handleStateChange (e) {

		/**
		 * @type {import('extensions/multi-system/types').StateChangeEventDetail[]}
		 */
		const actions = Array.isArray(e.detail)
			? e.detail
			: [e.detail];

		let selectedRoomOrStepUpdates = 0;

		this.state = actions?.reduce((state, {action, payload, merge}) => {
			if (action === StateActions.SelectedRoomUpdate || action === StateActions.StepUpdate) {
				selectedRoomOrStepUpdates++;
			}

			return this._processStateAction(action, payload, merge);
		}, this.state);

		if (selectedRoomOrStepUpdates > 0) {
			await this.updateComplete;

			window.scrollTo(0, 0);
		}

		this._setUrlFromState();
	}

	connectedCallback () {
		super.connectedCallback();

		window.addEventListener('popstate', this.handlePopstate);
		this.addEventListener('statechange', this.handleStateChange);

		// Fetch disallowed combinations from custom field if present
		const disallowedField = this.getAttribute('disallowed-combinations');
		if (disallowedField) {
			try {
				this.disallowedCombinations = JSON.parse(disallowedField);
			} catch (e) {
				this.disallowedCombinations = [];
				// Optionally log error
			}
		}

		// Fetch master matrix from attribute if present
		const masterMatrixField = this.getAttribute('master-matrix');
		if (masterMatrixField) {
			try {
				this.masterMatrix = JSON.parse(masterMatrixField);
			} catch (e) {
				this.masterMatrix = [];
				// Optionally log error
			}
		}
	}

	disconnectedCallback () {
		window.removeEventListener('popstate', this.handlePopstate);
		this.addEventListener('statechange', this.handleStateChange);

		super.disconnectedCallback();
	}

	handleProductChange (e) {
		const summaryProductsMap = e?.detail;

		this.totalCapacity = this.state?.rooms
			?.map(room => parseFloat(summaryProductsMap?.[room.product]?.CustomField_Values?.customfields?.[CAPACITY_FILTER_FIELD]) || 0)
			?.reduce((total, capacity) => total + capacity, 0);
	}

	/**
	 * Returns a filtered list of allowed outdoor units based on current indoor selections and masterMatrix (supports R32 and R410A).
	 * @param {Array<string>} allOutdoors - Array of all possible outdoor codes
	 * @param {string} refrigerantType - Refrigerant type, e.g. 'R32' or 'R410A'
	 * @returns {Array<string>} - Allowed outdoor codes
	 */
	getAllowedOutdoors (allOutdoors, refrigerantType = 'R32') {
		const masterMatrix = this.masterMatrix && typeof this.masterMatrix === 'object' ? this.masterMatrix : {};
		const rooms = this.state?.rooms || [];
		const numRooms = rooms.length;
		if (numRooms < 2 || numRooms > 5 || !masterMatrix[refrigerantType]) {
			return allOutdoors;
		}
		// Build selected capacities as array of strings, pad to 6 elements (as in matrix)
		const selectedCapacities = rooms.map(r => String(r.capacityClass || '0'));
		while (selectedCapacities.length < 6) {
			selectedCapacities.push('0');
		}
		// Check if this combination exists in masterMatrix[refrigerantType]
		const isAllowedCombo = masterMatrix[refrigerantType].some(combo => Array.isArray(combo) && combo.length === 6 && combo.every((val, idx) => val === selectedCapacities[idx]));
		if (!isAllowedCombo) {
			return [];
		}
		return allOutdoors;
	}

	renderStepRoomCreation () {
		return html`<x-step-room-creation room-count="${this.state?.roomCount}" .rooms=${this.state?.rooms} .masterMatrix="${this.masterMatrix}"></x-step-room-creation>`;
	}

	renderStepIndoor () {
		return html`<x-step-indoor room-count="${this.state?.roomCount}" selected-room="${this.state?.selectedRoom}" .rooms="${this.state?.rooms}"></x-step-indoor>`;
	}

	renderStepOutdoor () {
		const rooms = this.state?.rooms || [];
		// Extract outdoor codes from fetched product data
		const outdoorProducts = Array.isArray(this.outdoorProducts) ? this.outdoorProducts : [];
		const outdoorCodes = outdoorProducts.map(p => p.code).filter(Boolean);
		// Determine refrigerant type (R32 or R410A)
		const refrigerantType = 'R32';
		// Filter allowed outdoors
		const allowedOutdoors = this.getAllowedOutdoors(outdoorCodes, refrigerantType);

		return html`<x-step-outdoor
			.selectedProduct="${this.state?.outdoorProduct}"
			.selectedRoom="${this.state?.selectedRoom}"
			.rooms="${rooms}"
			totalCapacity="${this.totalCapacity}"
			.allowedOutdoors="${allowedOutdoors}"
			.disallowedCombinations="${this.disallowedCombinations}"
		></x-step-outdoor>`;
	}

	renderStepUpsell () {
		// eslint-disable-next-line array-element-newline
		return html`<x-step-upsell selected-room="${this.state?.selectedRoom}" .selectedProducts="${[this.state?.outdoorProduct, ...this.state?.rooms?.map(room => room.product)]}" .rooms="${this.state?.rooms}" .upsellProducts="${this.state?.upsellProducts}"></x-step-upsell>`;
	}

	renderStepRequiredProducts () {
		// eslint-disable-next-line array-element-newline
		return html`<x-step-required-products selected-room="${this.state?.selectedRoom}" .selectedProducts="${[this.state?.outdoorProduct, ...this.state?.rooms?.map(room => room.product)]}" .rooms="${this.state?.rooms}" .requiredProducts="${this.state?.requiredProducts}"></x-step-required-products>`;
	}

	renderSteps () {
		switch (this.state?.step) {
			default:
			case Steps.RoomCreation: {
				return this.renderStepRoomCreation();
			}
			case Steps.Indoor: {
				return this.renderStepIndoor();
			}
			case Steps.Outdoor: {
				return this.renderStepOutdoor();
			}
			case Steps.RequiredProducts: {
				return this.renderStepRequiredProducts();
			}
			case Steps.Upsell:
			case Steps.Review: {
				return this.renderStepUpsell();
			}
		}
	}

	render () {
		return html`
			<div class="container">
				${this.state?.step === Steps.RoomCreation ? nothing : html`<x-room-progress min="${this.state?.selectedRoom}" max="${this.state?.roomCount + 3}" label="${this.selectedRoomName}"></x-room-progress>`}
				${this.renderSteps()}
			</div>
			<x-summary .state="${this.state}" @productchange="${this.handleProductChange}"></x-summary>
		`;
	}
};

defineCustomElement('x-multi-system', MultiSystem);
